/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { usePaymentListener } from "../../hooks/usePaymentListener"
import { usePaymentRejoin } from "../../hooks/usePaymentRejoin"
import { TShoppingTicket } from "../../utils/@types/data/ticket"
import { TPaymentSession } from "../../utils/@types/data/paymentSession"
import * as S from "./styled"
import { CardError } from "./components/CardError"

import Header from "../../components/Header"
import Footer from "../../components/Footer"
import { getDatePeriod } from "../../utils/tb/getDatePeriod"
import Container from "../../components/Container"
import Feedback from "../../components/Feedback"

import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import loadingAnimation from "../../assets/animations/loading"

import { Api } from "../../api"
import { generateTicketID } from "../../utils/tb/qrcode"
import { getAdditionalTax } from "../../utils/tb/taxes"
import { TUser } from "../../utils/@types/data/user"
import { TEventData } from "../../utils/@types/data/event"

import pageTools from "../../utils/tb/pageTools/pix"
import { logCommunication } from "../../utils/logCommunication"
import OrderResume from "../../components/OrderResume"
import PostPaymentDelivery from "../../components/PostPaymentDelivery"
import downloadTickets from "../../utils/pdf"
import { adoptOrderToken } from "../../utils/tb/orderToken"

const io = require("socket.io-client")

const socketUrl =
  process.env.REACT_APP_SOCKET_URL || "https://api.oitickets.com.br"

type PageStatus = "loading" | "processing" | "approved" | "rejected" | "error-sign"

// TTL do registro de sessão de pagamento no backend (default 30min).
// Mantemos paridade com `PAYMENT_SESSION_TTL_MS` do servidor.
const PAYMENT_SESSION_MAX_AGE_MS = 30 * 60 * 1000

// Detecta erros de estoque vindos de `Api.post.purchase.sign` e devolve uma
// mensagem em pt-BR. Se não for caso de estoque, retorna undefined e o caller
// usa o texto padrão de "erro ao iniciar pedido".
const buildStockErrorMessage = (sign: {
  ok: false
  error?: string
  code?: string
  details?: any
}): string | undefined => {
  const code = sign.code ?? sign.error
  const isStock =
    code === "STOCK_INSUFFICIENT" ||
    /saldo indispon[ií]vel/i.test(sign.error ?? "") ||
    /estoque/i.test(sign.error ?? "")

  if (!isStock) return undefined

  const available = sign.details?.available
  if (typeof available === "number") {
    if (available <= 0)
      return "Este ingresso acabou de esgotar. Atualize seu carrinho e tente novamente."
    return `Restam apenas ${available} unidade${available === 1 ? "" : "s"} deste ingresso. Atualize seu carrinho e tente novamente.`
  }

  return "Não há mais ingressos disponíveis para essa compra. Atualize seu carrinho e tente novamente."
}

const PaymentCard = () => {
  const lctn = useLocation()
  const navigate = useNavigate()

  const eventData = sessionStorage.getItem("event")
  const event = eventData ? (JSON.parse(eventData) as TEventData) : null

  const user = sessionStorage.getItem("user")
    ? (JSON.parse(sessionStorage.getItem("user") as string) as TUser)
    : null

  const [pageStatus, setPageStatus] = useState<PageStatus>("loading")
  const [buyedTickets, setBuyedTickets] = useState<TShoppingTicket[]>([])
  const [rejectedDetail, setRejectedDetail] = useState<string | undefined>()
  // Pedido aprovado: id (mesmo usado em purchaseInfo) e extref para o
  // fluxo de entrega por WhatsApp / link público.
  const [deliveryOrder, setDeliveryOrder] = useState({ id: "", extref: "" })
  const [feedback] = useState<any>({ visible: false, message: "" })
  const [listenerActive, setListenerActive] = useState(false)
  const [listenerSocket, setListenerSocket] = useState<any>(null)
  const [listenerOrderId, setListenerOrderId] = useState("")
  const [signErrorMessage, setSignErrorMessage] = useState<string | undefined>()
  // Encurta o timeout do socket no listener quando o rejoin foi recusado:
  // como não vamos receber `orderUpdate`, caímos no polling REST mais cedo.
  const [listenerSocketTimeoutMs, setListenerSocketTimeoutMs] =
    useState<number>(45000)

  const socketRef = useRef<any>(null)
  const orderIdRef = useRef<string | null>(null)
  const orderNumberRef = useRef<number | null>(null)
  const isProcessingRef = useRef(false)
  const isOrderConfirmingRef = useRef(false)
  const isOrderConfirmedRef = useRef(false)

  usePaymentListener({
    socket: listenerSocket,
    orderId: listenerOrderId,
    eventId: event?.id ?? "",
    active: listenerActive,
    socketTimeoutMs: listenerSocketTimeoutMs,
    onApproved: (payload) => {
      const ordId = orderIdRef.current ?? listenerOrderId
      const pId =
        payload?.paymentId ?? payload?.payments?.[0]?.transition_id ?? ""
      handleApproved(ordId, pId)
    },
    onRejected: (payload) => {
      handleRejected({
        statusDetail:
          payload?.statusDetail ??
          payload?.payments?.[0]?.status_detail ??
          "cc_rejected_other_reason",
      })
    },
  })

  usePaymentRejoin({
    socket: listenerSocket,
    active: listenerActive,
    onRejoinError: () => {
      // Backend recusou o rejoin (sessão expirada ou extref inválido).
      // Não derruba a UI — o polling REST do listener assume em poucos segundos.
      setListenerSocketTimeoutMs(2000)
    },
  })

  const parsePurchaseInfo = useCallback(
    (p: any) => {
      const tickets = p.products as any[]
      const external_reference = p.extref
      const buyer_name = p.buyer?.name || p.buyer_name

      return tickets.map((t) => {
        const tid = generateTicketID(
          false,
          "ecommerce",
          t.opuid,
          event?.oid as number,
          event?.dbName as string,
        )

        return {
          id: t.id,
          name: t.name,
          batch_name: t.batch_name,
          is_mesa: t.is_mesa === true,
          event_name: event?.name as string,
          qr_data: t.qr_data,
          qr_label: t.qr_label,
          qr_TID: tid,
          order_id: orderIdRef.current,
          date: new Date(t.date).toISOString(),
          image: null,
          quantity: t.quantity,
          price_unit: t.price_unit,
          tax_value: t.tax_value,
          ticket_name: (t as any).ticket_name,
          user_name: (t as any).user_name,
          buyer_name: buyer_name,
          TRN: external_reference,
          payment_type: t.payment_type,
        }
      })
    },
    [event],
  )

  const loadPurchaseData = async (oid: string) => {
    if (event) {
      try {
        const p = await Api.get.purchaseInfo({ eventId: event.id, orderId: oid })
        if (p.ok && p.data.id) {
          const parsedData = parsePurchaseInfo(p.data)
          setBuyedTickets(parsedData as unknown as TShoppingTicket[])
          setDeliveryOrder({ id: oid, extref: p.data.extref })
        }
      } catch {}
    }
  }

  const confirmPurchaseFlow = async (sId: string, pcode: string) => {
    if (!isOrderConfirmingRef.current && !isOrderConfirmedRef.current) {
      isOrderConfirmingRef.current = true

      const confirmation = await Api.post.purchase.confirm({
        order_id: sId,
        order_number: orderNumberRef.current ?? 0,
        payment_code: pcode,
      })

      if (confirmation.ok && confirmation.data.success) {
        isOrderConfirmedRef.current = true
      }

      isOrderConfirmingRef.current = false
      return confirmation
    }
    return { ok: false, data: { success: false } }
  }

  const handleEmail = async (sId: string) => {
    try {
      const req = await Api.get.purchaseInfo({
        eventId: event?.id as string,
        orderId: sId,
      })

      if (req.ok) {
        const parsedData = parsePurchaseInfo(req.data)
        setBuyedTickets(parsedData as unknown as TShoppingTicket[])

        setDeliveryOrder({ id: sId, extref: req.data.extref })

        const purchaseValue = pageTools.order.getOrderValue(
          lctn.state.tickets,
          lctn.state.buyer,
          lctn.state.taxTotal,
          sId,
          user as TUser,
          event?.dk as string,
          event?.id as string,
          event?.name as string,
          event?.eCommerce.chargeClient ?? false,
          {
            method: "credit",
            additionalTaxCard: event?.eCommerce.additionalTaxCard,
          },
        )

        const targetEmail = (lctn.state.buyer?.email as string) ?? ""
        const buyerName = lctn.state.buyer?.name as string

        if (targetEmail && targetEmail.trim().length > 0) {
          const emailResult = await pageTools.email.sendEmail(
            event as TEventData,
            user as TUser,
            purchaseValue,
            lctn.state.tickets,
            {
              status: "aprovado",
              amount: lctn.state.taxTotal,
              message: "",
              sId,
              taxTotal: lctn.state.taxTotal,
              paymentMethod: "credit",
              transition_id: req.data.extref,
              time: new Date(
                req.data.products?.[0]?.date ?? Date.now(),
              ).toISOString(),
            },
            parsedData,
            targetEmail,
            buyerName,
          )
          // fire-and-forget log:
          logCommunication({
            event_id: String(event?.id ?? ''),
            order_id: orderIdRef.current ?? '',
            buyer_name: buyerName,
            recipient: targetEmail,
            channel: 'email',
            status: emailResult?.ok ? 'success' : 'failed',
          })
        }
      }
    } catch {}
  }

  const handleApproved = async (sId: string, pId: string) => {
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
    }

    localStorage.setItem("payed", "true")
    localStorage.removeItem("paymentSession")
    setPageStatus("approved")
    await confirmPurchaseFlow(sId, pId)
    handleEmail(sId)
  }

  const handleRejected = (data: { statusDetail?: string }) => {
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
    }
    localStorage.removeItem("paymentSession")
    setRejectedDetail(data.statusDetail)
    setPageStatus("rejected")
  }

  const startPayment = async (socketId: string) => {
    if (isProcessingRef.current) return
    isProcessingRef.current = true

    try {
      const {
        tickets,
        buyer,
        taxTotal,
        cardToken,
        paymentMethodId,
        installments,
        issuerId,
        payer,
      } = lctn.state

      const fullAmount = pageTools.order.getOrderValue(
        tickets,
        buyer,
        taxTotal,
        socketId,
        user as TUser,
        event?.dk as string,
        event?.id as string,
        event?.name as string,
        event?.eCommerce?.chargeClient ?? false,
      )

      // Taxa adicional do cartão: não vai no sign (backend a calcula por
      // tentativa de pagamento), só no valor cobrado.
      const chargeAmount =
        fullAmount +
        getAdditionalTax({
          baseTotal:
            fullAmount -
            (event?.eCommerce?.chargeClient ? Number(taxTotal) || 0 : 0),
          method: "credit",
          additionalTaxCard: event?.eCommerce?.additionalTaxCard,
        })

      const sign = await Api.post.purchase.sign({
        user_fone: user?.fone ?? "",
        event_id: event?.id as string,
        order_id: socketId,
        buyer_name: buyer?.name ?? "",
        buyer_email: buyer?.email ?? "",
        products: (tickets as any[]).map((t: any) => ({
          ...t,
          charge_client: Boolean(event?.eCommerce?.chargeClient) ? 1 : 0,
        })),
        payments: [
          {
            payment_type: "credit",
            price: fullAmount,
            transitionCode: null,
            transitionId: null,
          },
        ],
      })

      if (!sign.ok || !sign.data.success) {
        if (!sign.ok) setSignErrorMessage(buildStockErrorMessage(sign))
        setPageStatus("error-sign")
        return
      }

      // Antes do loadPurchaseData: o getPurchase precisa da sessão.
      adoptOrderToken(sign.data.order_id, sign.data.order_token, {
        asSession: !user || user.guest === true,
      })
      orderIdRef.current = sign.data.order_id
      orderNumberRef.current = sign.data.order_number

      localStorage.setItem(
        "paymentSession",
        JSON.stringify({
          extref: sign.data.extref,
          socketId,
          paymentId: sign.data.order_id,
          paymentStartedAt: new Date().getTime().toString(),
          amount: chargeAmount / 100,
        }),
      )

      loadPurchaseData(sign.data.order_id)

      const itemsMap = new Map<string, any>()
      ;(tickets as any[]).forEach((t: any) => {
        if (itemsMap.has(t.id)) {
          itemsMap.get(t.id).quantity += 1
        } else {
          itemsMap.set(t.id, {
            id: t.id,
            title: `${t.ticketName ?? t.name} - ${event?.name ?? ""}`,
            quantity: 1,
            unit_price: +(t.price_sell ?? 0),
          })
        }
      })

      const items = Array.from(itemsMap.values())

      const pay = await Api.post.purchase.payCard({
        token: cardToken,
        payment_method_id: paymentMethodId,
        installments: Number(installments),
        ...(issuerId ? { issuer_id: String(issuerId) } : {}),
        transaction_amount: chargeAmount / 100,
        payer,
        metadata: {
          cCode: sign.data.order_id,
          eventId: event?.id ?? "",
          dk: event?.dk ?? "",
          extref: sign.data.extref,
        },
        items,
      })

      if (!pay.ok) {
        handleRejected({
          statusDetail:
            pay.details?.mpCause ?? "cc_rejected_other_reason",
        })
        return
      }

      switch (pay.data.status) {
        case "approved":
          await handleApproved(socketId, pay.data.paymentId)
          break
        case "rejected":
          handleRejected(pay.data)
          break
        case "in_process":
        case "pending":
          setPageStatus("processing")
          setListenerOrderId(sign.data.order_id)
          setListenerSocket(socketRef.current)
          setListenerActive(true)
          break
      }
    } catch {
      setPageStatus("error-sign")
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0 })

    const localPayed = localStorage.getItem("payed") === "true"
    if (localPayed) {
      setPageStatus("approved")
      const localSession = localStorage.getItem("paymentSession")
      if (localSession) {
        try {
          const session = JSON.parse(localSession) as TPaymentSession
          if (session.paymentId) loadPurchaseData(session.paymentId)
          else if (session.socketId) loadPurchaseData(session.socketId)
        } catch {}
      }
      return
    }

    // Recuperação após reload: se há uma sessão de pagamento em curso e ainda
    // não recebemos confirmação, religa o socket no pedido antigo via rejoin
    // em vez de reabrir a tela de pagamento.
    let recoveredSession: TPaymentSession | null = null
    try {
      const raw = localStorage.getItem("paymentSession")
      const parsed = raw ? (JSON.parse(raw) as TPaymentSession) : null
      const startedAt = Number(parsed?.paymentStartedAt) || 0
      if (
        parsed?.extref &&
        parsed?.socketId &&
        parsed?.paymentId &&
        Date.now() - startedAt < PAYMENT_SESSION_MAX_AGE_MS
      ) {
        recoveredSession = parsed
      } else if (parsed) {
        // Sessão antiga/inutilizável — descartar para não interferir.
        localStorage.removeItem("paymentSession")
      }
    } catch {}

    if (!recoveredSession && !lctn.state?.tickets) {
      navigate("/", { replace: true })
      return
    }

    const socket = io(socketUrl, {
      autoConnect: true,
      closeOnBeforeunload: false,
      path: "",
      secure: false,
    })
    socketRef.current = socket

    if (recoveredSession) {
      orderIdRef.current = recoveredSession.paymentId
      setPageStatus("processing")
      loadPurchaseData(recoveredSession.paymentId)

      // O `usePaymentRejoin` cuida de emitir `rejoinPaymentSession` quando o
      // socket conectar; o `usePaymentListener` recebe `orderUpdate` na room
      // antiga (ou cai no polling REST se o rejoin for recusado).
      setListenerOrderId(recoveredSession.paymentId)
      setListenerSocket(socket)
      setListenerActive(true)
    } else {
      socket.on("plugged", (socketId: string) => {
        startPayment(socketId)
      })
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [])

  const handleDownload = async () => {
    if (event) await downloadTickets(event, buyedTickets, true)
  }

  const goToMyTickets = () => {
    navigate("/mytickets", { replace: true, state: { backRoute: "/" } })
  }

  const retryWithCard = () => {
    navigate("/payment", {
      state: { ...lctn.state, preselectMethod: "credit" },
    })
  }

  const isTerminal =
    pageStatus === "approved" ||
    pageStatus === "rejected" ||
    pageStatus === "error-sign"

  return (
    <S.Page>
      <Feedback data={feedback} />
      <Header />

      <Container fullHeight={true}>
        <S.Main $justifyCenter={isTerminal}>
          <S.Block $k={2} $flat={pageStatus === "approved"}>
            {(pageStatus === "loading" || pageStatus === "processing") && (
              <S.LoadingArea>
                <div style={{ width: 160 }}>
                  <DotLottieReact
                    data={loadingAnimation}
                    loop
                    autoplay
                    width="100%"
                  />
                </div>
                <S.BlockTitle $k={3}>
                  {pageStatus === "loading"
                    ? "Iniciando pagamento..."
                    : "Processando seu pagamento..."}
                </S.BlockTitle>
                {pageStatus === "processing" && (
                  <S.FeedbackIntructions $k={4}>
                    <span>
                      Aguarde enquanto confirmamos o pagamento com o seu banco.
                    </span>
                  </S.FeedbackIntructions>
                )}
              </S.LoadingArea>
            )}

            {pageStatus === "error-sign" && (
              <S.ErrorWrap>
                <h2>
                  {signErrorMessage
                    ? "Ingresso indisponível"
                    : "Erro ao processar pedido"}
                </h2>
                <p>
                  {signErrorMessage ??
                    "Ocorreu um erro ao iniciar seu pedido. Por favor, tente novamente."}
                </p>
                <S.ErrorButtons>
                  <S.Button onClick={retryWithCard}>
                    Tentar novamente
                  </S.Button>
                </S.ErrorButtons>
              </S.ErrorWrap>
            )}

            {pageStatus === "approved" && (
              <PostPaymentDelivery
                eventId={String(event?.id ?? "")}
                eventName={event?.name ?? ""}
                eventDate={
                  event?.date_ini
                    ? getDatePeriod(event.date_ini, event.date_end)
                    : undefined
                }
                orderId={deliveryOrder.id}
                extref={deliveryOrder.extref}
                quantity={
                  (lctn.state?.tickets as any[] | undefined)?.reduce(
                    (sum, t) => sum + (Number(t.quantity) || 1),
                    0,
                  ) ?? buyedTickets.length
                }
                buyerName={lctn.state?.buyer?.name}
                buyerPhone={lctn.state?.buyer?.phone ?? ""}
                buyerEmail={lctn.state?.buyer?.email}
                onDownload={handleDownload}
                onSeeMyTickets={goToMyTickets}
              />
            )}

            {pageStatus === "rejected" && (
              <CardError
                statusDetail={rejectedDetail}
                originalState={lctn.state}
              />
            )}
          </S.Block>

          {!isTerminal && (
            <OrderResume
              hideEventData={true}
              onlyPurchasingItems={true}
              fitContainer={true}
              reverse={true}
              paymentMethod="credit"
              ticketsList={
                lctn.state?.disposalTickets?.length > 0
                  ? lctn.state.disposalTickets
                  : lctn.state?.tickets ?? []
              }
            />
          )}
        </S.Main>
      </Container>

      <Footer />
    </S.Page>
  )
}

export { PaymentCard }
export default PaymentCard
